import { type EnvironmentPreset } from '@brickadia/presets';
import {
  type BRBanList,
  type BRPlayerNameCache,
  type BRRoleAssignments,
  type BRRoleSetup,
} from '@brickadia/types';
import { type ConsoleCommands } from '@omegga/commands';
import {
  type IGamemode,
  type ILogMinigame,
  type IMinigameList,
  type IPlayerPositions,
  type IServerStatus,
} from '@omegga/types';
import util from '@util';
import { type ReadSaveObject, type WriteSaveObject } from 'brs-js';

export const _OMEGGA_UTILS_IMPORT = util;
/* eslint-disable no-var -- `var` is required to declare globalThis properties */
declare global {
  export var Omegga: OmeggaLike;
  export var Player: StaticPlayer;
  export var OMEGGA_UTIL: typeof _OMEGGA_UTILS_IMPORT;
}
/* eslint-enable no-var */

export * from '@brickadia/types';
export * from '@omegga/types';
export type { ConsoleCommands } from '@omegga/commands';
export { OmeggaPlugin };

export type OL = OmeggaLike;
export type OP = OmeggaPlugin;
export type PC<T extends Record<string, unknown> = Record<string, unknown>> =
  PluginConfig<T>;
export type PS<T extends Record<string, unknown> = Record<string, unknown>> =
  PluginStore<T>;
export type PM = PluginMetrics;

export interface BrickBounds {
  minBound: [number, number, number];
  maxBound: [number, number, number];
  center: [number, number, number];
}

/** Created when a player clicks on a brick with an interact component */
export interface BrickInteraction {
  /** Brick name from catalog (Turkey Body, 4x Cube) */
  brick_name: string;
  /** Brick asset name; null when the display name is not recognized */
  brick_asset: string | null;
  /** Brick size; null when the display name is not recognized */
  brick_size: [number, number, number] | null;
  /** Player information, id, name, controller, and pawn */
  player: { id: string; name: string; controller: string; pawn: string };
  /** Brick center position */
  position: [number, number, number];

  /** message sent from a brick click interaction */
  message: string;
  /** data parsed from the line (if it starts with json:) */
  data: null | number | string | boolean | Record<string, unknown>;
  /** True when there was a json payload */
  json: boolean;
  /** True when there was a parse error */
  error: boolean;
}

/** AutoRestart options */
export type AutoRestartConfig = {
  players: boolean;
  announcement: boolean;
  saveWorld: boolean;
};

export interface OmeggaPlayer {
  /** player name */
  name: string;

  /** player display name */
  displayName: string;

  /** player uuid */
  id: string;

  /** player controller id */
  controller: string;

  /** player state id */
  state: string;

  /**
   * Returns omegga
   */
  getOmegga(): OmeggaLike;

  /**
   * Clone a player
   */
  clone(): OmeggaPlayer;

  /**
   * Get raw player info (to feed into a constructor)
   */
  raw(): [string, string, string, string, string];

  /**
   * True if the player is the host
   */
  isHost(): boolean;

  /**
   * Clear player's bricks
   * @param quiet clear bricks quietly
   */
  clearBricks(quiet?: boolean): void;

  /**
   * Get player's roles, if any
   */
  getRoles(): readonly string[];

  /**
   * Get player's permissions in a map like `{"Bricks.ClearOwn": true, ...}`
   * @return permissions map
   */
  getPermissions(): Record<string, boolean>;

  /**
   * Get player's name color
   * @return 6 character hex string
   */
  getNameColor(): string;

  /**
   * Get the player's pawn; null when the player has no pawn
   * @return pawn
   */
  getPawn(): Promise<string | null>;

  /**
   * Get player's position; null when the player has no pawn
   * @return [x, y, z] coordinates
   */
  getPosition(): Promise<[number, number, number] | null>;

  /**
   * Gets a user's ghost brick info (by uuid, name, controller, or player object)
   * @return ghost brick data
   */
  getGhostBrick(): Promise<
    | {
        targetGrid: string;
        location: number[];
        orientation: string;
      }
    | undefined
  >;

  /**
   * gets a user's paint tool properties
   */
  getPaint(): Promise<
    | {
        materialIndex: string;
        materialAlpha: string;
        material: string;
        color: number[];
      }
    | undefined
  >;

  /**
   * gets whether or not the player is crouching
   */
  isCrouched(pawn?: string): Promise<boolean>;

  /**
   * gets whether or not the player is dead
   */
  isDead(pawn?: string): Promise<boolean>;

  /**
   * Gets the bounds of the template in the user's clipboard (bounds of original selection box)
   * @return template bounds
   */
  getTemplateBounds(): Promise<BrickBounds | undefined>;

  /**
   * Get bricks inside template bounds
   * @return BRS JS Save Data
   */
  getTemplateBoundsData(): Promise<ReadSaveObject | undefined>;

  /**
   * Load bricks at ghost brick location
   * @param saveData save data to load
   */
  loadDataAtGhostBrick(
    saveData: WriteSaveObject,
    options?: {
      rotate?: boolean;
      offX?: number;
      offY?: number;
      offZ?: number;
      quiet?: boolean;
    },
  ): Promise<void>;

  /**
   * Load bricks on this player's clipboard
   * @param saveName Save to load
   */
  loadBricks(saveName: string): void;

  /**
   * Load bricks on this player's clipboard passing save data
   * @param saveData save data to load
   */
  loadSaveData(
    saveData: WriteSaveObject,
    options?: {
      rotate?: boolean;
      offX?: number;
      offY?: number;
      offZ?: number;
      quiet?: boolean;
    },
  ): Promise<void>;

  /**
   * Kills this player
   */
  kill(): void;

  /**
   * Damages a player
   * @param amount Amount to damage
   */
  damage(amount: number): void;

  /**
   * Heal this player
   * @param amount to heal
   */
  heal(amount: number): void;

  /**
   * Gives a player an item
   * @param item Item name (Weapon_Bow)
   */
  giveItem(item: WeaponClass): void;

  /**
   * Removes an item from a player's inventory
   * @param item Item name (Weapon_Bow)
   */
  takeItem(item: WeaponClass): void;

  /**
   * Changes a player's team
   * @param teamIndex Team index
   */
  setTeam(teamIndex: number): void;

  /**
   * Changes a player's minigame
   * @param index Minigame index
   */
  setMinigame(index: number): void;

  /**
   * Changes a player's score
   * @param minigameIndex minigame index
   * @param score Score
   */
  setScore(minigameIndex: number, score: number): void;

  /**
   * Fetch a player's score
   * @param minigameIndex minigame index
   */
  getScore(minigameIndex: number): Promise<number>;

  /**
   * Set leaderboard value
   * @param key leaderboard key (Score, Kills, Deaths, CorrectGuesses)
   * @param value leaderboard value
   */
  setLeaderboard(key: string, value: number): void;

  /**
   * Get leaderboard value
   * @param key leaderboard key (Score, Kills, Deaths, CorrectGuesses)
   * @return leaderboard value
   */
  getLeaderboard(key: string): Promise<number | null>;
}

export interface StaticPlayer {
  /**
   * get a player's roles, if any
   * @param omegga omegga instance
   * @param id player uuid
   * @return list of roles
   */
  getRoles(omegga: OmeggaLike, id: string): readonly string[];

  /**
   * get a player's permissions in a map like `{"Bricks.ClearOwn": true, ...}`
   * @param omegga Omegga instance
   * @param id player uuid
   * @return permissions map
   */
  getPermissions(omegga: OmeggaLike, id: string): Record<string, boolean>;

  /**
   * Kills a player
   * @param omegga Omegga instance
   * @param target Player or player name/id
   */
  kill(omegga: OmeggaLike, target: string | OmeggaPlayer): void;

  /**
   * Damages a player
   * @param omegga Omegga instance
   * @param target Player or player name/id
   * @param amount Damage amount
   */
  damage(
    omegga: OmeggaLike,
    target: string | OmeggaPlayer,
    amount: number,
  ): void;

  /**
   * Heal a player
   * @param omegga Omegga instance
   * @param target Player or player name/id
   * @param amount Heal amount
   */
  heal(omegga: OmeggaLike, target: string | OmeggaPlayer, amount: number): void;

  /**
   * Gives a player an item
   * @param omegga Omegga instance
   * @param target Player or player name/id
   * @param item Item name (Weapon_Bow)
   */
  giveItem(
    omegga: OmeggaLike,
    target: string | OmeggaPlayer,
    item: WeaponClass,
  ): void;

  /**
   * Removes an item from a player's inventory
   * @param omegga Omegga instance
   * @param target Player or player name/id
   * @param item Item name (Weapon_Bow)
   */
  takeItem(
    omegga: OmeggaLike,
    target: string | OmeggaPlayer,
    item: WeaponClass,
  ): void;

  /**
   * Changes a player's team
   * @param omegga Omegga instance
   * @param target Player or player name/id
   * @param teamIndex Team name? index?
   */
  setTeam(
    omegga: OmeggaLike,
    target: string | OmeggaPlayer,
    teamIndex: number,
  ): void;

  /**
   * Changes a player's minigame
   * @param omegga Omegga instance
   * @param target Player or player name/id
   * @param index Minigame index
   */
  setMinigame(
    omegga: OmeggaLike,
    target: string | OmeggaPlayer,
    index: number,
  ): void;

  /**
   * Changes a player's score
   * @param omegga Omegga instance
   * @param target Player or player name/id
   * @param minigameIndex minigame index
   * @param score Score
   */
  setScore(
    omegga: OmeggaLike,
    target: string | OmeggaPlayer,
    minigameIndex: number,
    score: number,
  ): void;

  /**
   * Fetches a player's score
   * @param omegga Omegga instance
   * @param target Player or player name/id
   * @param minigameIndex minigame index
   */
  getScore(
    omegga: OmeggaLike,
    target: string | OmeggaPlayer,
    minigameIndex: number,
  ): Promise<number>;

  /**
   * Set leaderboard value
   * @param omegga Omegga instance
   * @param target Player or player name/id
   * @param key leaderboard key (Score, Kills, Deaths, CorrectGuesses)
   * @param value leaderboard value
   */
  setLeaderboard(
    omegga: OmeggaLike,
    target: string | OmeggaPlayer,
    key: string,
    value: number,
  ): void;

  /**
   * Get leaderboard value
   * @param omegga Omegga instance
   * @param target Player or player name/id
   * @param key leaderboard key (Score, Kills, Deaths, CorrectGuesses)
   * @return leaderboard value
   */
  getLeaderboard(
    omegga: OmeggaLike,
    target: string | OmeggaPlayer,
    key: string,
  ): Promise<number | null>;
}

export interface InjectedCommands {
  /** Get server status */
  getServerStatus(this: OmeggaLike): Promise<IServerStatus | null>;
  /**
   * Get a list of minigames and their indices
   * @deprecated minigames were replaced by a single gamemode (~CL14000); on
   * modern servers this returns at most one entry with an empty `owner`.
   * Prefer {@link InjectedCommands.getGamemode}.
   */
  listMinigames(this: OmeggaLike): Promise<IMinigameList>;
  // /** Get all pawn data */
  // getAllPawnData(this: OmeggaLike, fields: PawnDataField[]): void;
  /** Get all player positions and pawns */
  getAllPlayerPositions(this: OmeggaLike): Promise<IPlayerPositions>;
  /** Get minigames and members (one entry per gamemode on modern servers) */
  getMinigames(this: OmeggaLike): Promise<ILogMinigame[]>;
  /**
   * Get the single gamemode and its teams/players (modern servers, >=CL14000).
   * Returns null on older servers (use {@link InjectedCommands.getMinigames}).
   */
  getGamemode(this: OmeggaLike): Promise<IGamemode | null>;
}

/**
 * Event listener shape, mirroring node's EventEmitter. The rest parameter is
 * `any[]` for the same reason node's own is: it is both the type every listener
 * must be assignable to and the type an inline listener's parameters are
 * inferred from, so `unknown[]` would type a custom event's arguments as
 * `unknown` at every call site that does not hit a named overload.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- see above
export type MockEventListener = (...args: any[]) => void;

export interface MockEventEmitter {
  addListener(event: string, listener: MockEventListener): this;
  emit(event: string, ...args: unknown[]): boolean;
  eventNames(): (string | symbol)[];
  getMaxListeners(): number;
  listenerCount(event: string): number;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- matches node's EventEmitter return type
  listeners(event: string): Function[];
  off(event: string, listener: MockEventListener): this;
  on(event: string, listener: MockEventListener): this;
  once(event: string, listener: MockEventListener): this;
  prependListener(event: string, listener: MockEventListener): this;
  prependOnceListener(event: string, listener: MockEventListener): this;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- matches node's EventEmitter return type
  rawListeners(event: string): Function[];
  removeAllListeners(event?: string): this;
  removeListener(event: string, listener: MockEventListener): this;
  setMaxListeners(maxListeners: number): this;

  on(event: 'close', listener: () => void): this;
  on(event: 'line', listener: (line: string) => void): this;
  on(event: 'start', listener: (info: { map: string }) => void): this;
  on(event: 'version', listener: (version: number) => void): this;
  on(event: 'unauthorized', listener: () => void): this;
  on(event: 'join', listener: (player: OmeggaPlayer) => void): this;
  on(event: 'leave', listener: (player: OmeggaPlayer) => void): this;
  on(event: 'chat', listener: (name: string, message: string) => void): this;
  on(event: 'mapchange', listener: (info: { map: string }) => void): this;
  on(event: 'autorestart', listener: (config: AutoRestartConfig) => void): this;
  on(
    event: 'interact',
    listener: (interaction: BrickInteraction) => void,
  ): this;
  on(
    event: 'minigamejoin',
    listener: (info: {
      player: { name: string; id: string };
      minigameName: string;
    }) => void,
  ): this;
  on(event: 'wirelog', listener: (raw: string) => void): this;
  on(event: `wirecmd:${string}`, listener: (...args: string[]) => void): this;
  on(
    event: 'wirecmd',
    listener: (cmd: string, ...args: string[]) => void,
  ): this;
}

export interface OmeggaLike
  extends OmeggaCore,
    LogWrangling,
    InjectedCommands,
    MockEventEmitter {
  writeln(line: string): void;

  /** game CL version*/
  version: number;

  /**
   * version-resolved Brickadia console command names, nested by namespace.
   * e.g. `Omegga.Console.Bricks.Clear` resolves to the command string for the
   * running game version ("Bricks.Clear" or "br.Bricks.Clear")
   */
  Console: ConsoleCommands;

  /** verbose logging is enabled*/
  verbose: boolean;

  /** list of players */
  players: OmeggaPlayer[];

  /** server host */
  host?: { id: string; name: string };

  /** server is started */
  started: boolean;
  /** server is starting */
  starting: boolean;
  /** server is stopping */
  stopping: boolean;
  /** current map */
  currentMap: string;

  /** path to config files */
  configPath: string;
  /** path to saves */
  savePath: string;
  /** path to worlds */
  worldPath: string;
  /** path to prefabs */
  prefabPath: string;
  /** path to presets */
  presetPath: string;
  /** path to containing dir */
  path: string;
  /**
   * path to the directory containing the game server binary - null when the
   * install is launcher-managed and the binary location isn't known
   */
  binaryPath: string | null;

  /** get a plugin's name, documentation, and loaded status
   * If run in an unsafe plugin, the emitPlugin method sends events from
   * an "unsafe" plugin
   */
  getPlugin(name: string): Promise<PluginInterop | null>;
}

export interface OmeggaCore {
  /**
   * get a list of players
   * @return list of players {id: uuid, name: name} objects
   */
  getPlayers(): {
    id: string;
    name: string;
    displayName: string;
    controller: string;
    state: string;
  }[];

  /**
   * find a player by name, id, controller, or state
   * @param target - name, id, controller, or state
   */
  getPlayer(target: string): OmeggaPlayer | null;

  /**
   * find a player by rough name, prioritize exact matches and get fuzzier
   * @param name player name, fuzzy
   */
  findPlayerByName(name: string): OmeggaPlayer | null;

  /**
   * get the host's ID
   * @return Host Id
   */
  getHostId(): string;

  /**
   * broadcast messages to chat
   * messages are broken by new line
   * multiple arguments are additional lines
   * all messages longer than 512 characters are deleted automatically, though omegga wouldn't have sent them anyway
   * @param messages unescaped chat messages to send. may need to wrap messages with quotes
   */
  broadcast(...messages: string[]): void;

  /**
   * whisper messages to a player's chat
   * messages are broken by new line
   * multiple arguments are additional lines
   * all messages longer than 512 characters are deleted automatically, though omegga wouldn't have sent them anyway
   * @param target - player identifier or player object
   * @param messages - unescaped chat messages to send. may need to wrap messages with quotes
   */
  whisper(target: string | OmeggaPlayer, ...messages: string[]): void;

  /**
   * prints text to the middle of a player's screen
   * all messages longer than 512 characters are deleted automatically
   * @param target - player identifier or player object
   * @param message - unescaped chat messages to send. may need to wrap messages with quotes
   */
  middlePrint(target: string | OmeggaPlayer, message: string): void;

  /**
   * Save a minigame preset based on a minigame index
   * @param index minigame index
   * @param name preset name
   */
  saveMinigame(index: number, name: string): void;

  /**
   * Delete a minigame
   * @param index minigame index
   */
  deleteMinigame(index: number): void;

  /**
   * Reset a minigame
   * @param index minigame index
   */
  resetMinigame(index: number): void;

  /**
   * Force the next round in a minigame
   * @param index minigame index
   */
  nextRoundMinigame(index: number): void;

  /**
   * Load an Minigame preset
   * @param presetName preset name
   * @param owner owner id/name
   */
  loadMinigame(presetName: string, owner?: string): void;

  /**
   * Get all presets in the minigame folder and child folders
   */
  getMinigamePresets(): string[];

  /**
   * Reset the environment settings
   */
  resetEnvironment(): void;

  /**
   * Save an environment preset
   * @param presetName preset name
   */
  saveEnvironment(presetName: string): Promise<void>;

  /** Save a temporary environment preset and return its contents */
  getEnvironmentData(): Promise<EnvironmentPreset | null>;

  /**
   * Read environment data as json; null when the preset is missing or invalid
   * @param presetName preset name
   */
  readEnvironmentData(presetName: string): EnvironmentPreset | null;

  /**
   * Load an environment preset
   * @param presetName preset name
   */
  loadEnvironment(presetName: string): void;

  /**
   * Load some environment preset data
   * @param preset preset data
   */
  loadEnvironmentData(
    preset:
      | EnvironmentPreset
      | NonNullable<EnvironmentPreset['data']>['groups'],
  ): void;

  /**
   * Get all presets in the environment folder and child folders
   */
  getEnvironmentPresets(): string[];

  /**
   * Clear a user's bricks (by uuid, name, controller, or player object)
   * @param target player or player identifier
   * @param quiet quietly clear bricks
   */
  clearBricks(target: string | { id: string }, quiet?: boolean): void;

  /**
   * Clear a region of bricks. On EA3 this routes to `br.World.ClearRegion`
   * and can optionally clear entities too.
   * @param region region to clear
   * @param options optional settings
   */
  clearRegion(
    region: {
      center: [number, number, number];
      extent: [number, number, number];
    },
    options?: {
      target?: string | OmeggaPlayer;
      /** clear bricks in the region (default true) */
      bricks?: boolean;
      /** also clear entities in the region (default false, EA3 only) */
      entities?: boolean;
    },
  ): void;

  /**
   * Clear all bricks on the server. On EA3 this routes to
   * `br.World.ClearAll` and can optionally clear entities too. A bare boolean
   * is accepted as the legacy `quiet` argument.
   * @param options quiet (or `{ quiet, bricks, entities }`)
   */
  clearAllBricks(
    options?:
      | boolean
      | { quiet?: boolean; bricks?: boolean; entities?: boolean },
  ): void;

  /**
   * Save bricks under a filename
   * @param saveName save file name
   * @param region region of bricks to save
   * @deprecated removed in Brickadia EA3 (no-op on newer servers) - save a
   * prefab with {@link savePrefabRegion} instead
   */
  saveBricks(
    saveName: string,
    region?: {
      center: [number, number, number];
      extent: [number, number, number];
    },
  ): void;

  /**
   * Save bricks under a filename, with a promise
   * @param saveName save file name
   * @param region region of bricks to save
   * @deprecated removed in Brickadia EA3 (no-op on newer servers) - save a
   * prefab with {@link savePrefabRegion} instead
   */
  saveBricksAsync(
    saveName: string,
    region?: {
      center: [number, number, number];
      extent: [number, number, number];
    },
  ): Promise<void>;

  /**
   * Load bricks on the server
   * @deprecated removed in Brickadia EA3 (no-op on newer servers) - load a
   * prefab with {@link loadPrefab} instead
   */
  loadBricks(
    saveName: string,
    options?: {
      offX?: number;
      offY?: number;
      offZ?: number;
      quiet?: boolean;
      correctPalette?: boolean;
      correctCustom?: boolean;
    },
  ): void;

  /**
   * Load bricks on the server into a player's clipbaord
   * @deprecated removed in Brickadia ~EA2 (no-op on newer servers) - use
   * {@link loadPrefabOnPlayer} instead
   */
  loadBricksOnPlayer(
    saveName: string,
    player: string | OmeggaPlayer,
    options?: {
      offX?: number;
      offY?: number;
      offZ?: number;
      correctPalette?: boolean;
      correctCustom?: boolean;
    },
  ): void;

  /**
   * Get all saves in the save folder and child folders
   */
  getSaves(): string[];

  /**
   * Checks if a save exists and returns an absolute path
   * @param saveName Save filename
   * @return Path to string, undefined if the save does not exist
   */
  getSavePath(saveName: string): string | undefined;

  /**
   * Get all worlds in the worlds folder and child folders
   */
  getWorlds(): string[];

  /**
   * Checks if a world exists and returns an absolute path
   * @param worldName World name
   * @return Path to string, undefined if the world does not exist
   */
  getWorldPath(worldName: string): string | undefined;

  /**
   * Get a list of revisions for a world
   * @param worldName World name
   */
  getWorldRevisions(
    worldName: string,
  ): Promise<{ index: number; date: Date; note: string }[]>;

  /**
   * Load a world by its name
   * @param worldName World name
   */
  loadWorld(worldName: string): Promise<boolean>;

  /**
   * Load a world at a specific revision
   * @param worldName World name
   */
  loadWorldRevision(worldName: string, revision: number): Promise<boolean>;

  /**
   * Save a world as a new name
   * @param worldName World name
   */
  saveWorldAs(worldName: string): Promise<boolean>;

  /**
   * Save the current world
   */
  saveWorld(): Promise<boolean>;

  /**
   * Create an empty world with the given name
   */
  createEmptyWorld(worldName: string): Promise<boolean>;

  /**
   * unsafely load save data (wrap in try/catch)
   * @param saveName save file name
   * @param saveData BRS JS Save data
   */
  writeSaveData(saveName: string, saveData: WriteSaveObject): void;

  /**
   * unsafely read save data (wrap in try/catch)
   * @param saveName save file name
   * @param nobricks only read save header data
   * @return BRS JS Save Data
   */
  readSaveData(saveName: string, nobricks?: boolean): ReadSaveObject;

  /**
   * load bricks from save data and resolve when game finishes loading
   * @param saveData BRS JS Save data
   * @deprecated removed in Brickadia EA3 (no-op on newer servers) - use the
   * prefab API ({@link loadPrefab}) instead
   */
  loadSaveData(
    saveData: WriteSaveObject,
    options?: {
      offX?: number;
      offY?: number;
      offZ?: number;
      quiet?: boolean;
      correctPalette?: boolean;
      correctCustom?: boolean;
    },
  ): Promise<void>;

  /**
   * load bricks from save data and resolve when game finishes loading
   * @param saveData BRS JS Save data
   * @param player Player name/id or player object
   * @deprecated removed in Brickadia ~EA2 (no-op on newer servers) - use the
   * prefab API ({@link loadPrefabOnPlayer}) instead
   */
  loadSaveDataOnPlayer(
    saveData: WriteSaveObject,
    player: string | OmeggaPlayer,
    options?: {
      offX?: number;
      offY?: number;
      offZ?: number;
      correctPalette?: boolean;
      correctCustom?: boolean;
    },
  ): Promise<void>;

  /**
   * get current bricks as save data
   * @deprecated removed in Brickadia EA3 (returns undefined on newer
   * servers) - use the prefab API instead
   */
  getSaveData(region?: {
    center: [number, number, number];
    extent: [number, number, number];
  }): Promise<ReadSaveObject | undefined>;

  /**
   * Get all prefabs in the prefabs folder and child folders (EA3)
   */
  getPrefabs(): string[];

  /**
   * Checks if a prefab exists and returns an absolute path (EA3)
   * @param prefabName Prefab filename
   * @return Path to string
   */
  getPrefabPath(prefabName: string): string | undefined;

  /**
   * Load a prefab into the world (EA3). `path` is a bundle path ref such
   * as `Prefabs/Uploads/<hash>.brz`.
   * @param path prefab bundle path ref
   * @param options placement options (offset, orientation, mirror axes, etc)
   */
  loadPrefab(
    path: string,
    options?: {
      offX?: number;
      offY?: number;
      offZ?: number;
      atOriginalPosition?: boolean;
      orientation?: number;
      rootEntityPersistentIndex?: number;
      /** bitmask: X=1 Y=2 Z=4 (e.g. 3 mirrors X and Y) */
      mirrorAxes?: number;
      overrideUserId?: string;
    },
  ): void;

  /**
   * Save the world (or a region of it) as a prefab (EA3).
   * @param path destination prefab bundle path ref (e.g. `Prefabs/MyPrefab.brz`)
   * @param options save options; omit `region` to capture the whole world
   */
  savePrefab(
    path: string,
    options?: {
      region?: {
        center: [number, number, number];
        extent: [number, number, number];
      };
      entities?: boolean;
      rootEntityPersistentIndex?: number;
      userId?: string;
    },
  ): void;

  /**
   * Save a prefab and resolve once the prefab file has been written to disk
   * (EA3).
   * @param path destination prefab bundle path ref
   * @param options same options as {@link savePrefab}
   * @returns absolute path to the written prefab, or null on timeout
   */
  savePrefabAsync(
    path: string,
    options?: {
      region?: {
        center: [number, number, number];
        extent: [number, number, number];
      };
      entities?: boolean;
      rootEntityPersistentIndex?: number;
      userId?: string;
    },
  ): Promise<string | null>;

  /**
   * Give a prefab to a player's inventory (EA3).
   * @param path prefab bundle path ref
   * @param player player name/id or player object
   * @param options give options (preserve ownership)
   */
  givePrefabToPlayer(
    path: string,
    player: string | OmeggaPlayer,
    options?: { preserveOwnership?: boolean },
  ): void;

  /**
   * Load a prefab onto a player (EA3, replaces {@link loadBricksOnPlayer}).
   * @param path prefab bundle path ref
   * @param player player name/id or player object
   * @param options give options (preserve ownership)
   */
  loadPrefabOnPlayer(
    path: string,
    player: string | OmeggaPlayer,
    options?: { preserveOwnership?: boolean },
  ): void;

  /**
   * Change server map
   * @param map Map name
   */
  changeMap(map: string): Promise<boolean>;

  /**
   * Get up-to-date role setup from RoleSetup.json
   */
  getRoleSetup(): BRRoleSetup;

  /**
   * Get up-to-date role assignments from RoleAssignment.json
   */
  getRoleAssignments(): BRRoleAssignments;

  /**
   * Get up-to-date ban list from BanList.json
   */
  getBanList(): BRBanList;

  /**
   * Get up-to-date name cache from PlayerNameCache.json
   */
  getNameCache(): BRPlayerNameCache;
}

/** A simple document store for plugins */
export interface PluginStore<
  Storage extends Record<string, unknown> = Record<string, unknown>,
> {
  /** Get a value from plugin storage */
  get<T extends keyof Storage>(key: T): Promise<Storage[T]>;
  /** Set a value to plugin storage */
  set<T extends keyof Storage>(key: T, value: Storage[T]): Promise<void>;
  /** Delete a value from plugin storage */
  delete(key: string): Promise<void>;
  /** Wipe all values in plugin storage */
  wipe(): Promise<void>;
  /** Count entries in plugin storage */
  count(): Promise<number>;
  /** Get a list of keys in plugin storage */
  keys(): Promise<(keyof Storage)[]>;
}

/** A config representative of the config outlined in doc.json */
export type PluginConfig<
  T extends Record<string, unknown> = Record<string, unknown>,
> = T;

/** Label values attached to a plugin metric */
export type PluginMetricLabels = Record<string, string | number | boolean>;

/** Options common to every plugin metric */
export interface PluginMetricOptions {
  /**
   * Metric name, exported as `omegga_plugin_<plugin>_<name>`. Must be
   * lowercase letters, digits, and underscores.
   */
  name: string;
  /** One-line description, shown in the scrape output */
  help?: string;
  /** Label names this metric may use (`plugin` is added automatically) */
  labels?: string[];
}

/** A value that only ever increases, such as a number of events */
export interface MetricCounter {
  /** Add to the count (default 1), optionally on a labelled series */
  inc(value?: number): void;
  inc(labels: PluginMetricLabels, value?: number): void;
  /** Discard every series */
  reset(): void;
}

/** A value that can go up and down, such as a queue length */
export interface MetricGauge {
  set(value: number): void;
  set(labels: PluginMetricLabels, value: number): void;
  inc(value?: number): void;
  inc(labels: PluginMetricLabels, value?: number): void;
  dec(value?: number): void;
  dec(labels: PluginMetricLabels, value?: number): void;
  /** Stop exporting one labelled series */
  remove(labels?: PluginMetricLabels): void;
  /**
   * Produce the value on demand instead of setting it. The callback runs when
   * the metric is collected, which is cheaper than setting it on a timer.
   */
  collect(fn: () => number | void): void;
  reset(): void;
}

/** Bucketed observations, such as durations */
export interface MetricHistogram {
  observe(value: number): void;
  observe(labels: PluginMetricLabels, value: number): void;
  /** Start a timer; call the returned function to observe elapsed seconds */
  startTimer(labels?: PluginMetricLabels): () => number;
  reset(): void;
}

/**
 * Prometheus metrics for a plugin, exported under `omegga_plugin_<plugin>_`.
 *
 * Handles are always returned, so no guards are needed: when the metrics
 * endpoint is disabled every call is a no-op.
 */
export interface PluginMetrics {
  /** Whether the metrics endpoint is enabled; handles work either way */
  readonly enabled: boolean;
  counter(opts: PluginMetricOptions): MetricCounter;
  gauge(opts: PluginMetricOptions): MetricGauge;
  histogram(
    opts: PluginMetricOptions & {
      /** Upper bounds in ascending order; `+Inf` is added automatically */
      buckets?: number[];
    },
  ): MetricHistogram;
}

/** An omegga plugin */
export default abstract class OmeggaPlugin<
  Config extends Record<string, unknown> = Record<string, unknown>,
  Storage extends Record<string, unknown> = Record<string, unknown>,
> {
  omegga: OmeggaLike;
  config: PluginConfig<Config>;
  store: PluginStore<Storage>;
  /**
   * Prometheus metrics for this plugin; no-ops when metrics are disabled.
   *
   * Always supplied by the plugin loader. It is declared optional purely so
   * that plugins written before metrics existed still satisfy
   * `implements OmeggaPlugin`. Declare `metrics: PM` on your own class, as
   * the templates do for `omegga`, `config`, and `store`, to get the
   * non-optional type.
   */
  metrics?: PluginMetrics;

  constructor(
    omegga: OmeggaLike,
    config: PluginConfig<Config>,
    store: PluginStore<Storage>,
    metrics: PluginMetrics,
  ) {
    this.omegga = omegga;
    this.config = config;
    this.store = store;
    this.metrics = metrics;
  }

  /** Run when plugin starts, returns /commands it uses */
  abstract init(): Promise<void | { registeredCommands?: string[] }>;

  /** Run when plugin is stopped */
  abstract stop(): Promise<void>;

  /** Run when another plugin tries to interact with this plugin
   * @param event Event name
   * @param from Name of origin plugin
   * @return value other plugin expects
   */
  abstract pluginEvent?(
    event: string,
    from: string,
    ...args: unknown[]
  ): Promise<unknown>;
}

export interface LogWrangling {
  /** Add a passive pattern on console output that invokes callback on match */
  addMatcher<T>(
    pattern: IMatcher<T>['pattern'],
    callback: IMatcher<T>['callback'],
  ): void;

  /**
   * Run an active pattern on console output that resolves a match.
   * T is the element type of the resolved matches - RegExpMatchArray for
   * RegExp patterns, the pattern's return type for function patterns.
   */
  addWatcher<T = RegExpMatchArray>(
    pattern: IWatcher<T>['pattern'],
    options?: {
      timeoutDelay?: number;
      bundle?: boolean;
      debounce?: boolean;
      afterMatchDelay?: number;
      last?: IWatcher<T>['last'];
      exec?: () => void;
    },
  ): Promise<T[]>;

  /**
   * Run a command and capture bundled output.
   * T is the element type of the resolved matches - RegExpMatchArray for
   * RegExp patterns, the pattern's return type for function patterns.
   */
  watchLogChunk<T = RegExpMatchArray>(
    cmd: string,
    pattern: IWatcher<T>['pattern'],
    options?: {
      first?: 'index' | ((match: T) => boolean);
      last?: IWatcher<T>['last'];
      afterMatchDelay?: number;
      timeoutDelay?: number;
    },
  ): Promise<T[]>;

  /** Run a command and capture bundled output for array functions */
  watchLogArray<
    Item extends Record<string, string> = Record<string, string>,
    Member extends Record<string, string> = Record<string, string>,
  >(
    cmd: string,
    itemPattern: RegExp,
    memberPattern: RegExp,
  ): Promise<{ item: Item; members: Member[] }[]>;
}

export type WatcherPattern<T> = (
  line: string,
  match: RegExpMatchArray | null,
) => T | RegExpMatchArray | null | undefined | '[OMEGGA_WATCHER_DONE]';

export type IMatcher<T> =
  | {
      pattern: RegExp;
      callback: (match: RegExpMatchArray) => boolean;
    }
  | {
      pattern: (line: string, match: RegExpMatchArray | null) => T;
      callback: (match: RegExpMatchArray) => T;
    };

export type IWatcher<T> = {
  bundle: boolean;
  debounce: boolean;
  timeoutDelay: number;
  afterMatchDelay: number;
  last: (match: T) => boolean;
  callback: () => void;
  resolve: (...args: T[]) => void;
  remove: () => void;
  done: () => void;
  timeout: ReturnType<typeof setTimeout>;
} & (
  | {
      pattern: WatcherPattern<T>;
      matches: T[];
    }
  | {
      pattern: RegExp;
      matches: RegExpMatchArray[];
    }
);

export declare type IPluginConfigDefinition = {
  description: string;
} & (
  | {
      type: 'string' | 'password' | 'role';
      default: string;
    }
  | {
      type: 'boolean';
      default: boolean;
    }
  | {
      type: 'number';
      default: number;
    }
  | {
      type: 'enum';
      options: (string | number)[];
      default: string | number;
    }
  | {
      type: 'players';
      default: {
        id: string;
        name: string;
      };
    }
  | ({
      type: 'list';
    } & (
      | {
          itemType: 'string';
          default: string[];
        }
      | {
          itemType: 'number';
          default: number[];
        }
      | {
          itemType: 'enum';
          options: (string | number)[];
          default: string | number;
        }
    ))
);
export interface IPluginCommandArgument {
  name: string;
  description: string;
  required?: boolean;
}
export interface IPluginCommand {
  name: string;
  description: string;
  example?: string;
  args: IPluginCommandArgument[];
}
export interface IPluginDocumentation {
  name: string;
  description: string;
  author: string;
  config: Record<string, IPluginConfigDefinition>;
  commands: IPluginCommand[];
}

export interface PluginInterop {
  name: string;
  documentation: IPluginDocumentation | null;
  loaded: boolean;
  emitPlugin?(event: string, args: unknown[]): Promise<unknown>;
}

export type WeaponClass =
  | 'Weapon_APCarbine'
  | 'Weapon_AntiMaterielRifle'
  | 'Weapon_ArmingSword'
  | 'Weapon_AssaultRifle'
  | 'Weapon_AutoShotgun'
  | 'Weapon_Battleaxe'
  | 'Weapon_Bazooka'
  | 'Weapon_BoltActionRifle'
  | 'Weapon_Bow'
  | 'Weapon_BoxPistol'
  | 'Weapon_BullpupRifle'
  | 'Weapon_BullpupSMG'
  | 'Weapon_ChargedLongsword'
  | 'Weapon_CrystalKalis'
  | 'Weapon_Derringer'
  | 'Weapon_Dynamite'
  | 'Weapon_FlintlockPistol'
  | 'Weapon_GrenadeLauncher'
  | 'Weapon_GuardPistol'
  | 'Weapon_Handaxe'
  | 'Weapon_HealthPotion'
  | 'Weapon_HeavyAssaultRifle'
  | 'Weapon_HeavySMG'
  | 'Weapon_HeroSword'
  | 'Weapon_HighPowerPistol'
  | 'Weapon_HoloBlade'
  | 'Weapon_HuntingShotgun'
  | 'Weapon_Ikakalaka'
  | 'Weapon_ImpactGrenade'
  | 'Weapon_ImpactGrenadeLauncher'
  | 'Weapon_ImpulseGrenade'
  | 'Weapon_Khopesh'
  | 'Weapon_Knife'
  | 'Weapon_LeverActionRifle'
  | 'Weapon_LightMachineGun'
  | 'Weapon_LongSword'
  | 'Weapon_MagnumPistol'
  | 'Weapon_MicroSMG'
  | 'Weapon_Minigun'
  | 'Weapon_Minigun'
  | 'Weapon_MissileLauncher'
  | 'Weapon_PDW'
  | 'Weapon_Pickaxe'
  | 'Weapon_PipeBomb'
  | 'Weapon_Pistol'
  | 'Weapon_PlasmaPistol'
  | 'Weapon_PlasmaSMG'
  | 'Weapon_PulseCarbine'
  | 'Weapon_PulseRifle'
  | 'Weapon_QuadLauncher'
  | 'Weapon_Revolver'
  | 'Weapon_RocketJumper'
  | 'Weapon_RocketLauncher'
  | 'Weapon_Sabre'
  | 'Weapon_SemiAutoRifle'
  | 'Weapon_ServiceRifle'
  | 'Weapon_Shotgun'
  | 'Weapon_SlugShotgun'
  | 'Weapon_Sniper'
  | 'Weapon_SodaCan'
  | 'Weapon_Spatha'
  | 'Weapon_SportingShotgun'
  | 'Weapon_StampedSMG'
  | 'Weapon_StandardSubmachineGun'
  | 'Weapon_StickGrenade'
  | 'Weapon_SubmachineGun'
  | 'Weapon_SuperShotgun'
  | 'Weapon_SuppressedAssaultRifle'
  | 'Weapon_SuppressedBullpupSMG'
  | 'Weapon_SuppressedGuardPistol'
  | 'Weapon_SuppressedHeavySMG'
  | 'Weapon_SuppressedMicroSMG'
  | 'Weapon_SuppressedPistol'
  | 'Weapon_SuppressedServiceRifle'
  | 'Weapon_TacticalSMG'
  | 'Weapon_TacticalShotgun'
  | 'Weapon_Tomahawk'
  | 'Weapon_TwinCannon'
  | 'Weapon_TypewriterSMG'
  | 'Weapon_Zweihander';
